import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { db } from '../firebase';
import {
  addDoc,
  collection,
  doc,
  getDocs,
  updateDoc,
  serverTimestamp,
} from 'firebase/firestore';

// Type
export interface SocialLink {
  id?: string;
  facebook: string;
  instagram: string;
  twitter: string;
  youtube: string;
  appStoreId?: string;
  playStoreId?: string;
  updatedAt?: number;
}

interface SocialState {
  social: SocialLink | null;
  loading: boolean;
  error: string | null;
}

const initialState: SocialState = {
  social: null,
  loading: false,
  error: null,
};

// Create
export const createSocialLinks = createAsyncThunk(
  'social/createSocialLinks',
  async (links: Omit<SocialLink, 'id'>, { rejectWithValue }) => {
    try {
      const docRef = await addDoc(collection(db, 'social'), {
        ...links,
        updatedAt: serverTimestamp(),
      });
      return { ...links, id: docRef.id };
    } catch (err: any) {
      return rejectWithValue(err.message);
    }
  }
);

// Fetch
export const fetchSocialLinks = createAsyncThunk(
  'social/fetchSocialLinks',
  async (_, { rejectWithValue }) => {
    try {
      const snapshot = await getDocs(collection(db, 'social'));
      const docs = snapshot.docs.map((doc) => ({
        ...doc.data(),
        id: doc.id,
      })) as SocialLink[];
      return docs[0] || null;
    } catch (err: any) {
      return rejectWithValue(err.message);
    }
  }
);

// Update
export const updateSocialLinks = createAsyncThunk(
  'social/updateSocialLinks',
  async (
    { id, data }: { id: string; data: Omit<SocialLink, 'id'> },
    { rejectWithValue }
  ) => {
    try {
      await updateDoc(doc(db, 'social', id), {
        ...data,
        updatedAt: serverTimestamp(),
      });
      return { id, ...data };
    } catch (err: any) {
      return rejectWithValue(err.message);
    }
  }
);

const socialSlice = createSlice({
  name: 'social',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(createSocialLinks.pending, (state) => {
        state.loading = true;
      })
      .addCase(createSocialLinks.fulfilled, (state, action) => {
        state.loading = false;
        state.social = action.payload;
      })
      .addCase(createSocialLinks.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      .addCase(fetchSocialLinks.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchSocialLinks.fulfilled, (state, action) => {
        state.loading = false;
        state.social = action.payload;
      })
      .addCase(fetchSocialLinks.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      .addCase(updateSocialLinks.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateSocialLinks.fulfilled, (state, action) => {
        state.loading = false;
        state.social = action.payload;
      })
      .addCase(updateSocialLinks.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export default socialSlice.reducer;
